
import Vue from "vue";
import Editor, { IEditorEmitArgs } from "@/components/molecules/Editor.vue";
import ManuscriptPreview from "@/components/organisms/ManuscriptPreview.vue";
import AssistPanel from "@/components/organisms/AssistPanel.vue";
import TextVerticalIcon from "@/components/atoms/TextVerticalIcon.vue";
import DropupMenu, { Item } from "@/components/molecules/DropupMenu.vue";
import { Manuscript, ManuscriptSetting, Novel, Plan, SearchTextIndex } from "@/lib/models";
import clone from "lodash/cloneDeep";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { downloadPublicImage } from "@/lib/s3";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
import { UserClient } from "@/lib/clients";
// dialog
import { Dialog } from "@/lib/utils";
import DialogVue from "@/components/ui/Dialog.vue";
import { showOkCancelDialog, openManuscriptEditorMenuDialog } from "@/lib/dialog";
import EpisodeListDialog, { EpisodeListDialogProps } from "@/components/ui/nolaNovel/EpisodeListDialog.vue";
import NolaNovelIntroduction from "@/components/ui/nolaNovel/NolaNovelIntroduction.vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import NolaNovelUpdateConfirm, {
  NolaNovelUpdateConfirmProps,
} from "@/components/ui/nolaNovel/NolaNovelUpdateConfirm.vue";
import EpisodeDelinkConfirm, { EpisodeDelinkConfirmProps } from "@/components/ui/nolaNovel/EpisodeDelinkConfirm.vue";
import ProofreadingConfirmationDialog from "@/components/ui/ProofreadingConfirmationDialog.vue";
import AutoIndentConfirmationDialog from "@/components/ui/AutoIndentConfirmationDialog.vue";
import UndoIcon from "icons/ArrowULeftTop.vue";
import RedoIcon from "icons/ArrowURightTop.vue";
import RubyDialog, { RubyDialogProps } from "@/components/ui/RubyDialog.vue";
import EmphasisMarkDialog, { EmphasisMarkDialogProps } from "@/components/ui/EmphasisMarkDialog.vue";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import ManuscriptHeader from "@/components/molecules/ManuscriptHeader.vue";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import AgentPromotionDialog from "@/components/ui/AgentPromotionDialog.vue";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import { useAgentStore, AgentStore } from "@/stores/agent";
import { MOBILE_BREAKPOINT } from "@/constants/breakpoints";
import { debounce } from "lodash";
import { getEffectiveEditorTheme } from "@/lib/utils/theme";

const contentMaxLength = 100000;
const CONTENT_LENGTH_OVER_50000 = 50000;

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Editor,
    ManuscriptPreview,
    AssistPanel,
    TextVerticalIcon,
    DropupMenu,
    ProofreadingConfirmationDialog,
    AutoIndentConfirmationDialog,
    UndoIcon,
    RedoIcon,
    ManuscriptHeader,
  },

  props: {
    novelId: String,
    manuscriptKey: String,
  },

  data() {
    const { onSelectCountType } = this as any;

    const countTypeList: Item[] = [
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.allCharacters") as string,
        value: "onlyCharacter",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withSpaces") as string,
        value: "addBlank",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withNewlines") as string,
        value: "addNewLine",
        callback() {
          onSelectCountType(this.value);
        },
      },
      {
        name: this.$t("manuscriptPage.manuscriptEditor.countTypes.withSpacesAndNewlines") as string,
        value: "all",
        callback() {
          onSelectCountType(this.value);
        },
      },
    ];

    return {
      title: "",
      preTitle: "",
      content: "",
      preContent: "",
      saved: false,
      countTypeList,
      hasNolaNovelAccount: false,
      defaultColor: "#a0a0a0",
      activeColor: "#5698e1",
      canUndo: false,
      canRedo: false,
      unbindShortcutKeys: null,
      agentStore: useAgentStore(),
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 0,
      debouncedUpdateWindowWidth: null,
      // 作品全体の文字数をキャッシュ
      cachedTotalNovelContentLength: 0,
      // 他の原稿の文字数をキャッシュ（パフォーマンス最適化）
      otherManuscriptsBaseLength: 0,
      // 検索状態更新のデバウンス関数
      debouncedUpdateSearchState: null as ReturnType<typeof debounce> | null,
    };
  },

  async created() {
    const { initialize } = this;
    initialize();
  },

  mounted() {
    // デバウンシングされたウィンドウ幅更新関数を作成
    if (typeof window !== "undefined") {
      this.debouncedUpdateWindowWidth = debounce(this.updateWindowWidth, 150);
      window.addEventListener("resize", this.debouncedUpdateWindowWidth);
    }
  },

  beforeDestroy() {
    // コンポーネントが破棄される前にショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();

    // ウィンドウリサイズイベントリスナーを削除
    if (typeof window !== "undefined" && this.debouncedUpdateWindowWidth) {
      window.removeEventListener("resize", this.debouncedUpdateWindowWidth);

      // デバウンシング関数のクリーンアップ
      this.debouncedUpdateWindowWidth.cancel();
    }

    // 検索状態更新のデバウンス関数のクリーンアップ
    if (this.debouncedUpdateSearchState) {
      this.debouncedUpdateSearchState.cancel();
    }
  },

  destroyed() {
    this.initializeSearchState();
  },

  watch: {
    // AssistPanel表示状態の監視
    isAssistPanelVisible: {
      handler(newValue, oldValue) {
        if (newValue) {
          // AssistPanelが表示された時、プレビューが表示中ならプレビューを非表示に
          if (this.isShowPreview) {
            this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");
          }
        }
      },
      immediate: true,
    },
    // 縦書きモード状態の監視
    isVerticalWritingMode: {
      handler(newValue) {
        // スマホ表示時は縦書きモード設定を無視
        if (this.isMobile) return;

        if (newValue) {
          // 縦書きモードがオンになった時、プレビューが表示中ならプレビューを非表示に
          if (this.isShowPreview) {
            this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");
          }
        }
      },
    },
    // プレビュー状態変化の監視（AssistPanelとの排他制御）
    isShowPreview: {
      handler(newValue, oldValue) {
        // プレビューが表示されたらAssistPanelをVuex状態含めて非表示に
        if (newValue && this.isAssistPanelVisible) {
          this.$store.dispatch("assistNavigationModule/hidePanel");
        }
      },
    },

    saveTimeout() {
      if (this.saveTimeout) {
        this.save();
        this.$store.dispatch("manuscriptModule/stopSaveTimeout");
      }
    },
    content: {
      handler() {
        this.setModified(this.changed);
        // パフォーマンス最適化: 検索UIが表示されている場合のみ検索状態を更新
        // デバウンスを使用してキー入力のたびに正規表現処理を行うのを避ける
        if (this.enabledSearchReplace && this.debouncedUpdateSearchState) {
          this.debouncedUpdateSearchState();
        }
      },
      immediate: true,
    },
    searchText: {
      handler() {
        // パフォーマンス最適化: 検索UIが表示されている場合のみ検索状態を更新
        if (this.enabledSearchReplace && this.debouncedUpdateSearchState) {
          this.debouncedUpdateSearchState();
        }
      },
      immediate: true,
    },
    enabledSearch(value: boolean) {
      if (!value) return;
      const { searchTargetIndex, searchTextIndexList, scrollToTargetText, focusInputSearch } = this;
      const { from } = searchTextIndexList[searchTargetIndex];
      scrollToTargetText(from);
      focusInputSearch();
      this.$store.dispatch("manuscriptModule/toggleEnabledSearch");
    },
    searchTargetIndex(value: number) {
      const { enabledSearchReplace, searchTextIndexList, scrollToTargetText, focusInputSearch } = this;
      if (!enabledSearchReplace || searchTextIndexList.length === 0) return;
      const { from } = searchTextIndexList[value];
      scrollToTargetText(from);
      focusInputSearch();
    },
    enabledReplaceSingle(value: boolean) {
      const {
        searchTextIndexList,
        searchTargetIndex,
        replaceText,
        scrollToTargetText,
        selectTargetText,
        executeReplaceText,
      } = this;

      if (value) {
        const { from, to } = searchTextIndexList[searchTargetIndex];
        executeReplaceText(from, to);
        this.$store.dispatch("manuscriptModule/toggleEnabledReplaceSingle");

        scrollToTargetText(from);
        selectTargetText(from, from + replaceText.length);
      }
    },
    async enabledReplaceAll(value: boolean) {
      const { searchText, replaceText } = this;

      if (value) {
        const replacedContent = this.content.replaceAll(searchText, replaceText);
        this.content = replacedContent; // WARNING: contentを直接変更しているため、undoが効かなくなる
        this.$store.dispatch("manuscriptModule/toggleEnabledReplaceAll");
      }
    },
    async restoredContent(value: string | null) {
      if (typeof value !== "string") return;

      // contentに反映する前に、現在の変更を保存する
      if (this.changed) await this.save();

      this.content = value;
      this.$store.dispatch("manuscriptModule/initializeRestoredContent");
    },
    isManuscriptUnsavedAgentNavigation: {
      handler(newValue) {
        if (newValue) {
          this.save();
        }
      },
    },
  },

  computed: {
    novel() {
      const { novelId } = this;
      const { getters } = this.$store;
      const novel = getters["novelModule/novel"](novelId) as Novel | undefined;
      return novel;
    },
    manuscript() {
      const { getters } = this.$store;
      return getters["manuscriptModule/manuscript"](this.manuscriptKey);
    },
    changed() {
      return this.preTitle !== this.title || this.preContent !== this.content;
    },
    exceeded() {
      return this.contentLength > contentMaxLength;
    },
    contentLength() {
      let rubyLength = 0;
      const content = clone(this.content) as string;

      // eslint-disable-next-line no-control-regex
      const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
      const textWithRuby = content.match(rubyRegExp);
      if (textWithRuby) {
        textWithRuby.forEach((text) => {
          const onlyRubyRegExp = new RegExp("《.*?》$");
          const textOnlyRuby = text.match(onlyRubyRegExp);
          rubyLength += textOnlyRuby![0].length + 1; // 親文字の縦棒をカウントから除くための+1
        });
      }

      let contentLength = 0;
      switch (this.countType) {
        case "addBlank":
          contentLength = content.replace(/\n/g, "").length;
          break;
        case "addNewLine":
          contentLength = content.replace(/[^\S\n]/g, "").length;
          break;
        case "all":
          contentLength = content.length;
          break;
        case "onlyCharacter":
        default:
          contentLength = content.replace(/\s+|\n/g, "").length;
          break;
      }

      return contentLength - rubyLength;
    },
    pageCount() {
      const content = clone(this.content) as string;

      /** ルビを除外する */
      const splittedContent = content.split("\n");
      const removedRubyContent = splittedContent.map((content) => {
        let result = content;
        // eslint-disable-next-line no-control-regex
        const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
        const textWithRuby = content.match(rubyRegExp);
        if (textWithRuby) {
          textWithRuby.forEach((text: string) => {
            let replaceText = text;
            const parentRegExp = new RegExp("[|｜]");
            replaceText = replaceText.replace(parentRegExp, "");
            replaceText = replaceText.replace("《.*?》", "");
            result = content.replace(text, replaceText);
          });
        }
        return result;
      });

      /** 原稿の設定情報を取得 */
      const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;

      /** 行数を計算する */
      let lineCount = 0;
      removedRubyContent.forEach(
        (content) => (lineCount += content ? Math.ceil(content.length / wordCountOnVertical) : 1)
      );
      return Math.ceil(lineCount / lineCountPerPage);
    },
    onlyCharacterWordCount() {
      const content = clone(this.content) as string;

      let rubyLength = 0;
      // eslint-disable-next-line no-control-regex
      const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
      const textWithRuby = content.match(rubyRegExp);
      if (textWithRuby) {
        textWithRuby.forEach((text) => {
          const onlyRubyRegExp = new RegExp("《.*?》$");
          const textOnlyRuby = text.match(onlyRubyRegExp);
          rubyLength += textOnlyRuby![0].length + 1; // 親文字の縦棒をカウントから除くための+1
        });
      }

      const contentLength = content.replace(/\s+|\n/g, "").length;

      return contentLength - rubyLength;
    },
    isShowPreview() {
      const { $store, isFree } = this;
      const { getters } = $store;

      const isShowPreview = getters["manuscriptModule/isShowPreviewOnManuscriptEditor"];

      return !isFree && isShowPreview;
    },
    isAssistPanelVisible() {
      return this.$store.getters["assistNavigationModule/isVisible"];
    },
    isVerticalWritingMode() {
      return this.$store.getters["manuscriptModule/isVerticalWritingMode"];
    },

    isMobile() {
      // SCSS の @include mq(sp) と一致する判定
      // windowWidthをリアクティブな値として使用することで、リサイズに対応
      return this.windowWidth <= MOBILE_BREAKPOINT;
    },
    manuscriptList() {
      const key = this.manuscriptKey;
      const { title, content } = this;
      const manuscriptObject: Manuscript = {
        key,
        title,
        content,
      };
      return [manuscriptObject];
    },
    theme() {
      const storeTheme = this.$store.getters["manuscriptModule/theme"];
      const effectiveAppTheme = this.$store.getters["generalModule/effectiveTheme"] as "light" | "dark";
      return getEffectiveEditorTheme(storeTheme, effectiveAppTheme);
    },
    countType() {
      return this.$store.getters["manuscriptModule/countType"];
    },
    countTypeName() {
      const countTypeValue = this.countType;
      const countType = (this.countTypeList as Item[]).find((countType) => countType.value === countTypeValue);
      const countTypeName = countType ? countType.name : "";

      /** 原稿の設定情報を取得 */
      const { wordCountOnVertical, lineCountPerPage } = this.manuscriptSetting as ManuscriptSetting;

      return this.$t("manuscriptPage.manuscriptEditor.countFormat", {
        countTypeName,
        vertical: wordCountOnVertical,
        horizontal: lineCountPerPage,
      }) as string;
    },
    wordCountInfo() {
      const { contentLength } = this;
      const { pageCount } = this;

      return this.$t("manuscriptPage.manuscriptEditor.wordCountInfo", {
        count: contentLength,
        pages: pageCount,
      }) as string;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
    manuscriptSetting() {
      return this.$store.getters["novelModule/manuscriptSetting"](this.novelId) as ManuscriptSetting;
    },
    saveTimeout() {
      return this.$store.getters["manuscriptModule/saveTimeout"];
    },
    saveStatus() {
      const { autoSave, saved } = this;

      if (autoSave) {
        return saved ? "自動保存しました" : "自動保存設定中";
      }
      return saved ? "保存しました" : "";
    },
    lastModified() {
      return this.manuscript && this.manuscript.lastModified ? `最終保存：${this.manuscript.lastModified}` : "";
    },
    autoSave() {
      return this.$store.getters["manuscriptModule/autoSave"];
    },
    textareaElement() {
      return document.getElementById("textarea") as HTMLTextAreaElement;
    },
    enabledSearchReplace() {
      return this.$store.getters["manuscriptModule/enabledSearchReplace"];
    },
    enabledReplace() {
      return this.$store.getters["manuscriptModule/enabledReplace"];
    },
    searchText() {
      return this.$store.getters["manuscriptModule/searchText"];
    },
    replaceText() {
      return this.$store.getters["manuscriptModule/replaceText"];
    },
    enabledSearch() {
      return this.$store.getters["manuscriptModule/enabledSearch"];
    },
    enabledReplaceSingle() {
      return this.$store.getters["manuscriptModule/enabledReplaceSingle"];
    },
    enabledReplaceAll() {
      return this.$store.getters["manuscriptModule/enabledReplaceAll"];
    },
    searchTargetIndex() {
      return this.$store.getters["manuscriptModule/searchTargetIndex"];
    },
    searchTotalCount() {
      return this.$store.getters["manuscriptModule/searchTotalCount"];
    },
    searchTextIndexList() {
      return this.$store.getters["manuscriptModule/searchTextIndexList"];
    },
    restoredContent() {
      return this.$store.getters["manuscriptModule/restoredContent"];
    },

    // NolaNovel

    novelIdFromNolaNovel() {
      const { novel } = this;

      if (!novel) {
        return undefined;
      }

      const { associatedData } = novel as Novel;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    episodeIdFromNolaNovel() {
      const { manuscript } = this;
      if (!manuscript) {
        return undefined;
      }

      const { associatedData } = manuscript as Manuscript;

      if (!associatedData) {
        return undefined;
      }

      const { nolaNovel } = associatedData;

      if (!nolaNovel) {
        return undefined;
      }

      const { id } = nolaNovel;

      if (!id) {
        return undefined;
      }

      return id;
    },
    isContentLengthOver50000() {
      return this.cachedTotalNovelContentLength >= CONTENT_LENGTH_OVER_50000;
    },
    isAgentPopupDisplayedForNovel() {
      const { $store, novelId } = this;
      return $store.getters["generalModule/isAgentPopupDisplayedForNovel"](novelId);
    },
    isManuscriptUnsavedAgentNavigation() {
      return this.agentStore.isManuscriptUnsavedAgentNavigation;
    },
  },

  methods: {
    // initialize

    async initialize() {
      const { manuscript } = this;
      const { title, content } = manuscript;
      this.title = title || "";
      this.content = content || "";
      this.preTitle = String(this.title);
      this.preContent = String(this.content);

      // set RecordWordCount
      this.$store.commit("manuscriptModule/setBaseWordCount", this.onlyCharacterWordCount);

      // 各ショートカットキーの有効化と解除関数の取得
      this.unbindShortcutKeys = handleShortcutKeys([
        { trigger: TriggerKey.Meta, keys: ["s"], callback: this.save },
        { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.save },
        { trigger: TriggerKey.Meta, keys: ["f"], callback: this.enableSearchReplace },
        { trigger: TriggerKey.Ctrl, keys: ["f"], callback: this.enableSearchReplace },
        { trigger: TriggerKey.Meta, keys: ["h"], callback: this.enableReplace },
        { trigger: TriggerKey.Ctrl, keys: ["h"], callback: this.enableReplace },
        { keys: ["Escape", "esc"], callback: this.disableSearchReplace },
      ]);

      // 他の原稿の文字数を一度だけ計算してキャッシュ（パフォーマンス最適化）
      this.otherManuscriptsBaseLength = this.computeOtherManuscriptsLength();

      // 作品の執筆文字数を計算してStoreにセット
      this.updateTotalNovelContentLength();

      // 検索状態更新のデバウンス関数を初期化
      this.debouncedUpdateSearchState = debounce(() => this.updateSearchState(), 150);

      try {
        const results = await new UserClient().checkConnectedOtherServices();
        const { nolaNovel } = results;
        if (nolaNovel) {
          this.hasNolaNovelAccount = nolaNovel.result ?? false;
        }
      } catch (e) {
        // 特にエラーでも何もしない
      }
    },

    /**
     * 現在編集中の原稿以外の全原稿の文字数を計算する
     * パフォーマンス最適化のため、初期化時に1回だけ呼び出してキャッシュする
     */
    computeOtherManuscriptsLength() {
      const manuscriptList = this.$store.getters["manuscriptModule/manuscriptList"];
      const currentManuscriptKey = this.manuscriptKey;

      let totalLength = 0;

      manuscriptList.forEach((manuscript: Manuscript) => {
        if (manuscript.key === currentManuscriptKey) {
          return;
        }

        let rubyLength = 0;
        const content = clone(manuscript.content) as string;

        // eslint-disable-next-line no-control-regex
        const rubyRegExp = new RegExp("[|｜][^|｜\r\n]*?《.*?》", "gu");
        const textWithRuby = content.match(rubyRegExp);
        if (textWithRuby) {
          textWithRuby.forEach((text) => {
            const onlyRubyRegExp = new RegExp("《.*?》$");
            const textOnlyRuby = text.match(onlyRubyRegExp);
            rubyLength += textOnlyRuby![0].length + 1;
          });
        }

        const contentLength = content.replace(/\s+|\n/g, "").length;
        totalLength += contentLength - rubyLength;
      });

      return totalLength;
    },

    // execute

    async save() {
      const { novelId, title, content, manuscriptKey, changed } = this;

      if (!changed) return;

      if (this.contentLength > contentMaxLength) {
        this.showErrorDialog(
          "本文の文字数が10万字を越えています。<br />10万字以内に編集して再度保存を実行してください。<br />※別の原稿にて続きをお書きください。"
        );

        // エラーの場合はプロモーション風バナー利用済みフラグ/isManuscriptUnsavedAgentNavigationフラグを初期化する
        this.$store.dispatch("generalModule/setUsedAgentFeaturePromoBanner", false);
        this.agentStore.setManuscriptUnsavedAgentNavigation(false);

        // 10万字エラーダイアログ表示フラグを設定
        this.$store.commit("manuscriptModule/setWordLimitErrorDialogShown", true);
        return;
      }

      const key = manuscriptKey;
      this.$store.dispatch("manuscriptModule/updateManuscript", {
        novelId,
        key,
        title,
        content,
        callback: async () => {
          this.preTitle = clone(title);
          this.preContent = clone(content);
          this.setModified(false);

          this.saved = true;
          setTimeout(() => (this.saved = false), 2000);

          this.$notify({
            title: "保存しました",
            type: "success",
            duration: 2000,
          });

          // 保存成功時に作品全体の文字数を再計算
          this.updateTotalNovelContentLength();

          // 作品文字数が5万文字を超えた場合かつ、これまでエージェント機能ポップアップを
          // 表示していない作品の場合、ポップアップを表示する
          if (this.isContentLengthOver50000 && !this.isAgentPopupDisplayedForNovel) {
            // 自動保存が有効な場合、即時にポップアップを表示せず、
            // Storeに表示フラグをセットし、ホーム画面に遷移したときにポップアップを表示する
            if (this.autoSave) {
              this.$store.commit("generalModule/setAgentPopupDisplayFlagNovelId", novelId);
              return;
            }

            const agentPromotionDialog = new Dialog(AgentPromotionDialog);
            agentPromotionDialog.show({ novelTitle: this.novel!.title });
            await this.$store.dispatch("generalModule/addDisplayedAgentPopupForNovel", novelId);
          }
        },
      });
      this.$store.dispatch("manuscriptModule/incrementWritingCount", {
        novelId,
        value: this.onlyCharacterWordCount,
      });

      // 原稿未保存状態でエージェント機能プロモーションダイアログのボタンが
      // 押下された場合、エージェントイントロ画面へ遷移する
      if (this.isManuscriptUnsavedAgentNavigation) {
        this.setModified(false);
        this.$router.push({
          name: "agent",
        });
      }
    },
    /**
     * 作品全体の文字数を計算し、キャッシュとStoreを更新する
     * 保存時と初期化時にのみ呼び出す
     * パフォーマンス最適化: 他の原稿の文字数は初期化時に一度だけ計算してキャッシュ
     * 現在開いている原稿の文字数のみをリアルタイムで計算
     */
    updateTotalNovelContentLength(): void {
      // 現在開いている原稿の文字数から空白や改行を除いた文字数を計算
      // 注意: 元の実装と同様に、現在の原稿はルビを除外せずにカウント
      const currentContentLength = this.content.replace(/\s+|\n/g, "").length;
      const totalContentLength = this.otherManuscriptsBaseLength + currentContentLength;

      // キャッシュを更新
      this.cachedTotalNovelContentLength = totalContentLength;

      // Storeを更新
      this.$store.commit("novelModule/setNovelContentLength", totalContentLength);
    },
    async goToMultiEditor() {
      const { novelId, manuscriptKey } = this;
      if (this.isFree) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "マルチ執筆モードはプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくと【全画面表示】で【資料を見ながら】執筆が可能な『マルチ執筆モード』をご利用いただけます。執筆がリアルタイムに反映される縦書きプレビューも同時にご利用いただけますので、お手すきの際にぜひご利用いただけますと幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/manuscript_multi_editor.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({
          name: "subscriptionAnnounce",
          query: { from: "multiEditor" },
        });

        return;
      }

      this.$router.push({
        name: "multiEditor",
        params: {
          novelId,
          manuscriptKey,
        },
      });
    },
    async navigateToNolaNovelEditPage(id: string) {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error("NolaノベルのWebサイトURLが環境変数に含まれていません。");
      }

      const { novelId, manuscript } = this;
      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const updateEpisodeUrl = await service.genUpdateEpisodeUrl(id, novelId, manuscript.key!);
      window.open(
        createUrlWithUtmParams(updateEpisodeUrl, NolaPageName.Editor, NolaItemId.ConfirmOverwriteEpisodeButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
    executeReplaceText(from, to) {
      const { textareaElement, replaceText } = this;
      // フォーカスを先に設定してから選択範囲を設定する
      textareaElement.focus();
      textareaElement.selectionStart = from;
      textareaElement.selectionEnd = to;
      document.execCommand("insertText", false, replaceText);
    },
    initializeSearchState() {
      this.disableSearchReplace();
      this.$store.dispatch("manuscriptModule/updateSearchText", "");
      this.$store.dispatch("manuscriptModule/updateReplaceText", "");
    },
    updateSearchState() {
      const { searchText, searchTargetIndex } = this;
      const searchTextIndexList: SearchTextIndex[] = [];

      if (searchText) {
        const escapedText = searchText.replaceAll(/[.*+?^${}()[\]\\]/g, "\\$&");

        // ルビなしとルビありの両方に対応する正規表現パターン
        // ルビ付き文字の部分とプレーンテキストの部分を分離して検出
        const rubyPattern = escapedText
          .split("")
          .map((char: string) => `[｜|]?${char}(?:《[^》]*》)?`)
          .join("");

        const searchRegExp = new RegExp(rubyPattern, "gu");

        let match: RegExpExecArray | null;
        // eslint-disable-next-line no-cond-assign
        while ((match = searchRegExp.exec(this.content)) !== null) {
          // 検索結果の開始インデックス
          const from = match.index;
          // 検索結果の終了インデックス
          const to = match.index + match[0].length;
          // 検索結果をリストに追加
          searchTextIndexList.push({ from, to });
        }
      }

      this.$store.dispatch("manuscriptModule/updateSearchTextIndexList", searchTextIndexList);
      // 検索結果が変わった場合のみインデックスをリセット
      if (searchTargetIndex >= searchTextIndexList.length) {
        this.$store.dispatch("manuscriptModule/updateSearchTargetIndex", 0);
      }
      this.$store.dispatch("manuscriptModule/updateSearchTotalCount", searchTextIndexList.length);
    },

    // event handle

    async handleBackAction() {
      const { novelId, changed } = this;
      if (changed) {
        const yes = await showOkCancelDialog({
          title: "確認",
          content: "保存されていない変更があります。<br />変更を破棄して戻りますか？",
          okButton: "破棄する",
          cancelButton: "キャンセル",
        });
        if (!yes) {
          return;
        }
      }

      this.$router.push({ name: "editor", params: { novelId } });
    },
    onExportButtonClick(isPost) {
      const { hasNolaNovelAccount } = this;
      if (!hasNolaNovelAccount) {
        const introduction = new Dialog(NolaNovelIntroduction);
        introduction.show();
        return;
      }

      const { changed, showUnsavedConfirm } = this;
      if (changed) {
        showUnsavedConfirm();
        return;
      }

      const { episodeIdFromNolaNovel, showNolaNovelExportConfirm } = this;

      if (episodeIdFromNolaNovel) {
        showNolaNovelExportConfirm(episodeIdFromNolaNovel);
        return;
      }

      const { showNolaNovelListDialog } = this;
      showNolaNovelListDialog(isPost);
    },
    async onClickPreview() {
      if (this.isFree) {
        // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
        // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
        // const data: PremiumFeatureDialogProps = {
        //   title: "執筆時の縦書きプレビューはプレミアム機能です",
        //   text: "Nolaプレミアムにご登録いただくと、原稿の執筆時にリアルタイムで反映される『縦書きプレビュー機能』をご利用いただけます。一行辺りの文字数やページ辺りの行数も設定可能ですので、お手すきの際にぜひご活用いただだけたら幸いです。",
        //   image: (await downloadPublicImage("shared/images/gif/manuscript_vertical_writing.gif")) as string,
        // };
        // premiumFeatureDialog.show(data);

        this.$router.push({
          name: "subscriptionAnnounce",
          query: { from: "verticalWriting" },
        });

        return;
      }
      this.$store.dispatch("manuscriptModule/switchPreviewOnManuscriptEditor");
    },
    async onChangeTitle(args) {
      const { value, isComposing } = args;

      this.title = value;
      this.setModified(this.changed);

      if (this.changed && this.autoSave && !isComposing) {
        this.$store.dispatch("manuscriptModule/startSaveTimeout");
      }
    },
    async onChangeContent(args) {
      this.checkUndoRedo();

      const { value, isComposing } = args;

      this.content = value;
      this.setModified(this.changed);

      if (this.changed && this.autoSave && !isComposing) {
        this.$store.dispatch("manuscriptModule/startSaveTimeout");
      }
    },
    onStartComposition() {
      if (this.changed && this.autoSave) {
        this.$store.dispatch("manuscriptModule/stopSaveTimeout");
      }
    },
    onEndComposition() {
      if (this.changed && this.autoSave) {
        this.$store.dispatch("manuscriptModule/startSaveTimeout");
      }
    },
    onSelectCountType(countTypeValue) {
      this.$store.dispatch("manuscriptModule/selectCountType", countTypeValue);
    },
    enableSearchReplace() {
      const { $store, enabledSearchReplace } = this;

      if (!enabledSearchReplace) $store.dispatch("manuscriptModule/toggleEnabledSearchReplace");

      const selectedText = window.getSelection()!.toString();
      if (selectedText) $store.dispatch("manuscriptModule/updateSearchText", selectedText);

      // 検索ダイアログにフォーカスを設定
      this.$nextTick(() => {
        const searchInput = document.getElementById("inputSearch") as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      });

      // 検索UIが開かれた時に検索状態を即座に更新
      this.updateSearchState();
    },
    enableReplace() {
      const { $store, enabledReplace, enableSearchReplace } = this;
      enableSearchReplace();
      if (!enabledReplace) $store.dispatch("manuscriptModule/toggleEnabledReplace");
    },
    disableSearchReplace() {
      if (this.enabledSearchReplace) this.$store.dispatch("manuscriptModule/toggleEnabledSearchReplace");
    },
    scrollToTargetText(to) {
      // NOTE: 検索対象文字がtextareaの表示外にあるとき、検索対象文字までスクロールさせる
      const { textareaElement } = this;
      textareaElement.selectionStart = to;
      textareaElement.selectionEnd = to;
      textareaElement.blur();
      textareaElement.focus();
    },
    selectTargetText(from, to) {
      const { textareaElement } = this;
      textareaElement.selectionStart = from;
      textareaElement.selectionEnd = to;
      textareaElement.focus();
    },
    focusInputSearch() {
      const inputSearchElement = document.getElementById("inputSearch") as HTMLInputElement | null;
      inputSearchElement?.focus();
    },

    // view

    showUnsavedConfirm() {
      const completeDialog = new Dialog(CupertinoAlertDialog);
      const data: CupertinoAlertDialogProps = {
        title: "未保存の内容があります",
        content: "未保存のデータがあります。保存を実行してから投稿することが可能です。",
        action: "閉じる",
      };
      completeDialog.show(data);
    },
    async showMenu() {
      openManuscriptEditorMenuDialog();
    },
    showErrorDialog(message: string) {
      const confirmDialog = new Dialog(DialogVue);
      const options = {
        title: "エラー",
        content: message,
      };

      confirmDialog.show(options).then(() => {
        // 10万字エラーダイアログが閉じられたタイミングで
        // プロモーションバナー再表示のため、フラグをリセット
        this.$store.commit("manuscriptModule/setWordLimitErrorDialogShown", false);
      });
    },

    async showNolaNovelExportConfirm(id) {
      const confirmDialog = new Dialog(NolaNovelUpdateConfirm);
      const data: NolaNovelUpdateConfirmProps = {
        id,
        content: `この原稿を、Nolaノベル上に投稿された連携済みエピソードに上書きしてよろしいですか？`,
      };
      const result = await confirmDialog.show(data);
      const { navigateToNolaNovelEditPage } = this;
      if (result) {
        navigateToNolaNovelEditPage(id);
      }
    },
    async showNolaNovelListDialog(isPost: boolean) {
      const { novelId, manuscriptKey, novelIdFromNolaNovel } = this;
      const novelListDialog = new Dialog(EpisodeListDialog);
      const data: EpisodeListDialogProps = {
        novelId,
        manuscriptId: manuscriptKey,
        isPost,
        novelIdFromNolaNovel,
      };

      const result = await novelListDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: "連携が完了しました",
          content: "今後はこの原稿をNolaからNolaノベルに投稿する際は、自動でエピソードが選択されます。",
          action: "閉じる",
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    async showNolaNovelDelinkConfirmDialog(id) {
      const { novelId, manuscriptKey } = this;
      const deleteDialog = new Dialog(EpisodeDelinkConfirm);
      const data: EpisodeDelinkConfirmProps = {
        novelId,
        manuscriptId: manuscriptKey,
        episodeId: id,
      };

      const result = await deleteDialog.show(data);
      if (result) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: "連携を解除しました",
          content:
            "再度連携を行う場合は、Nolaノベルアイコンまたはメニュー内の「Nolaノベルと連携する」を選択してください。",
          action: "閉じる",
        };
        completeDialog.show(data);
      }

      const { initialize } = this;
      initialize();
    },
    undo() {
      document.execCommand("undo", false, undefined);
    },
    redo() {
      document.execCommand("redo", false, undefined);
    },
    checkUndoRedo() {
      this.canUndo = document.queryCommandEnabled("undo");
      this.canRedo = document.queryCommandEnabled("redo");
    },
    brackets() {
      const { textareaElement } = this;
      textareaElement.focus();
      const { selectionStart } = textareaElement;
      const selectedText = window.getSelection()!.toString();
      document.execCommand("insertText", false, `「${selectedText}」`);

      // NOTE: 文字選択をしていない場合、括弧の間にカーソルを移動
      if (!selectedText) {
        textareaElement.selectionStart = selectionStart + 1;
        textareaElement.selectionEnd = selectionStart + 1;
      }
    },
    ellipsis() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, "……");
    },
    space() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, "　");
    },
    dash() {
      const { textareaElement } = this;
      textareaElement.focus();
      document.execCommand("insertText", false, "――");
    },
    async ruby() {
      const { textareaElement } = this;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const rubyDialog = new Dialog(RubyDialog);
      const data: RubyDialogProps = {
        selectedText,
        negative: "キャンセル",
        positive: "入力する",
      };
      const result = await rubyDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    async emphasisMark() {
      const { textareaElement } = this;
      textareaElement.focus();
      const selectedText = window.getSelection()!.toString();

      const emphasisMarkDialog = new Dialog(EmphasisMarkDialog);
      const data: EmphasisMarkDialogProps = {
        selectedText,
        negative: "キャンセル",
        positive: "入力する",
      };
      const result = await emphasisMarkDialog.show(data);

      textareaElement.focus();

      if (!result) return;
      document.execCommand("insertText", false, result);
    },
    handleCommentIconClick() {
      this.$emit("commentIconClicked");
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
});

interface Props {
  novelId: string;
  manuscriptKey: string;
}

interface Data {
  title: string;
  preTitle: string;
  content: string;
  preContent: string;
  saved: boolean;
  countTypeList: Item[];
  hasNolaNovelAccount: boolean;
  defaultColor: string;
  activeColor: string;
  canUndo: boolean;
  canRedo: boolean;
  unbindShortcutKeys: (() => void) | null;
  agentStore: AgentStore;
  windowWidth: number;
  debouncedUpdateWindowWidth: ReturnType<typeof debounce> | null;
  cachedTotalNovelContentLength: number;
  otherManuscriptsBaseLength: number;
  debouncedUpdateSearchState: ReturnType<typeof debounce> | null;
}

interface Methods {
  initialize(): void;
  computeOtherManuscriptsLength(): number;

  save(): Promise<void>;
  updateTotalNovelContentLength(): void;
  handleBackAction(): Promise<void>;
  goToMultiEditor(): Promise<void>;
  navigateToNolaNovelEditPage(id: string): void;
  executeReplaceText(from: number, to: number): void;
  initializeSearchState(): void;
  updateSearchState(): void;

  onClickPreview(): void;
  onSelectCountType(countTypeValue: any): void;
  onChangeTitle(args: IEditorEmitArgs): Promise<void>;
  onChangeContent(args: IEditorEmitArgs): Promise<void>;
  onStartComposition(): void;
  onEndComposition(): void;
  onExportButtonClick(isPost: boolean): void;
  enableSearchReplace(): void;
  enableReplace(): void;
  disableSearchReplace(): void;
  scrollToTargetText(to: number): void;
  selectTargetText(from: number, to: number): void;
  focusInputSearch(): void;

  showUnsavedConfirm(): void;
  showMenu(): Promise<void>;
  showErrorDialog(message: string): void;
  showNolaNovelExportConfirm(id: string): void;
  showNolaNovelListDialog(isPost: boolean): void;
  showNolaNovelDelinkConfirmDialog(id: string): void;
  undo(): void;
  redo(): void;
  checkUndoRedo(): void;
  brackets(): void;
  ellipsis(): void;
  space(): void;
  dash(): void;
  ruby(): void;
  emphasisMark(): void;
  handleCommentIconClick(): void;
  updateWindowWidth(): void;
}

interface Computed {
  novel?: Novel;
  manuscript: Manuscript;
  changed: boolean;
  exceeded: boolean;
  contentLength: number;
  pageCount: number;
  onlyCharacterWordCount: number;
  isShowPreview: boolean;
  isAssistPanelVisible: boolean;
  isVerticalWritingMode: boolean;
  isMobile: boolean;
  manuscriptList: Manuscript[];
  theme: string;
  countType: string;
  countTypeName: string;
  wordCountInfo: string;
  plan: Plan;
  isFree: boolean;
  manuscriptSetting: ManuscriptSetting;
  saveTimeout: boolean;
  saveStatus: string;
  lastModified: string;
  autoSave: boolean;
  textareaElement: HTMLTextAreaElement;
  enabledSearchReplace: boolean;
  enabledReplace: boolean;
  searchText: string;
  replaceText: string;
  enabledSearch: boolean;
  enabledReplaceSingle: boolean;
  enabledReplaceAll: boolean;
  searchTargetIndex: number;
  searchTotalCount: number;
  searchTextIndexList: SearchTextIndex[];
  restoredContent: string | null;
  isContentLengthOver50000: boolean;
  isAgentPopupDisplayedForNovel: boolean;
  isManuscriptUnsavedAgentNavigation: boolean;

  // NolaNovel
  novelIdFromNolaNovel?: string;
  episodeIdFromNolaNovel?: string;
}
